/**
 * Helpers for creating and managing notifications.
 * Notifications are stored in Firestore collection "notifications".
 */

import {
  getProductRef,
  notificationsRef,
  mailRef,
  usersRef,
  getConversationRef,
  serverTimestamp,
} from './collections';

/**
 * Create a notification for a user.
 * @param {string} userId - Recipient user id
 * @param {string} type - offer | message | price_drop | review | system
 * @param {string} title - Short title
 * @param {string} body - Notification body text
 * @param {object} data - Optional payload (productId, offerId, etc.)
 */
export function createNotification(userId, type, title, body, data = {}) {
  if (!userId) return Promise.resolve();
  return notificationsRef().add({
    userId,
    type: type || 'system',
    title: title || '',
    body: body || '',
    data: data || {},
    read: false,
    createdAt: serverTimestamp(),
  });
}

/**
 * Notify users who have this product in watchlist with alertPrice >= newPrice.
 * Call after updating product price. product doc should have watchers: { [userId]: alertPrice }.
 */
export function notifyPriceDrop(productId, newPrice, productName) {
  const numPrice = Number(newPrice);
  if (!productId || isNaN(numPrice)) return Promise.resolve();
  return getProductRef(productId)
    .get()
    .then((doc) => {
      if (!doc.exists) return;
      const watchers = doc.data().watchers || {};
      const promises = [];
      Object.keys(watchers).forEach((userId) => {
        const alertPrice = Number(watchers[userId]);
        if (!isNaN(alertPrice) && numPrice <= alertPrice) {
          promises.push(
            createNotification(
              userId,
              'price_drop',
              'Price drop alert',
              `"${productName || 'An ad'}" is now €${numPrice.toLocaleString('en-US')} (your alert was €${alertPrice.toLocaleString('en-US')})`,
              { productId }
            )
          );
        }
      });
      return Promise.all(promises);
    });
}

/**
 * Queue a chat email notification for the recipient.
 * Uses the Firestore `mail` collection (Firebase Trigger Email extension pattern).
 * Silently fails if the recipient has disabled email notifications.
 */
export async function sendChatEmailNotification(conversationId, senderId, messageText) {
  try {
    const convDoc = await getConversationRef(conversationId).get();
    if (!convDoc.exists) return;
    const conv = convDoc.data();
    const recipientId = (conv.participants || []).find((p) => p !== senderId);
    if (!recipientId) return;

    const recipientDoc = await usersRef().doc(recipientId).get();
    if (!recipientDoc.exists) return;
    const recipient = recipientDoc.data();
    if (recipient.emailNotifications === false) return;

    await mailRef().add({
      to: recipient.email,
      message: {
        subject: `New message from ${recipient.name || 'a user'}`,
        text: `${recipient.name || 'Someone'} sent you a message: "${(messageText || '').slice(0, 200)}"`,
        html: `<p><strong>${recipient.name || 'Someone'}</strong> sent you a message:</p><p>"${(messageText || '').slice(0, 200)}"</p>`,
      },
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    console.error('sendChatEmailNotification failed:', err);
  }
}
